// Stub for @workspace/api-client-react compatibility
// This allows the app to run while we migrate to Supabase

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";

// Error class for API errors
export class ApiError extends Error {
  status: number;
  data?: any;
  
  constructor(message: string, status: number = 500, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export type User = {
  id: string;
  email: string;
  role: 'buyer' | 'seller' | 'both' | 'admin';
  name?: string;
  avatar?: string;
  isVerified: boolean;
  stripeConnectAccountId?: string;
  stripeConnectEnabled?: boolean;
  isSponsored?: boolean;
  planTier?: 'none' | 'basic' | 'pro' | 'enterprise';
};

// Storage helpers for auth tokens
export function setStoredAccessToken(token: string | null): void {
  if (token) {
    localStorage.setItem('access_token', token);
  } else {
    localStorage.removeItem('access_token');
  }
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

export function clearStoredAccessToken(): void {
  localStorage.removeItem('access_token');
}

export async function customFetch<T>(url: string, options?: RequestInit & { skipAuth?: boolean }): Promise<T> {
  // Use Supabase client instead of direct REST API calls
  console.log('Using Supabase client for:', url);
  
  // For now, return mock data as fallback
  // This is a temporary stub to get the app running
  return {} as T;
}

// Export hooks that the old API provided
export function useListings() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchListings = async () => {
      setIsLoading(true);
      try {
        const { data: listingsData, error: listingsError } = await supabase
          .from('listings')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (listingsError) throw listingsError;
        setData(listingsData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchListings();
  }, []);

  return { data, isLoading, error };
}

// Base mutation return type with all required properties
type MutationReturn<T = any> = {
  mutate: (vars?: T) => Promise<any>;
  mutateAsync: (vars?: T) => Promise<any>;
  isLoading: boolean;
  isPending: boolean;
  error: Error | null;
  data?: any;
  reset?: () => void;
};

export function useCreateListing(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = vars;
      
      // Check if seller exists, create if not
      const { data: existingSeller, error: sellerCheckError } = await supabase
        .from('sellers')
        .select('id')
        .eq('id', data.sellerId)
        .single();
      
      if (sellerCheckError && sellerCheckError.code === 'PGRST116') {
        // Seller doesn't exist, create one
        // Fetch user's profile to get their name
        const { data: profileData } = await supabase
          .from('profiles')
          .select('display_name, username')
          .eq('id', data.sellerId)
          .single();

        const storeName = profileData?.display_name || profileData?.username || 'My Shop';

        const { error: createSellerError } = await supabase
          .from('sellers')
          .insert({
            id: data.sellerId,
            store_name: storeName,
          });
        
        if (createSellerError) {
          throw createSellerError;
        }
      } else if (sellerCheckError) {
        throw sellerCheckError;
      }
      
      const { error: insertError, data: insertedData } = await supabase
        .from('listings')
        .insert({
          seller_id: data.sellerId,
          title: data.title,
          description: data.description || null,
          price: data.basePrice,
          category: data.category,
          stock: data.stock || null,
          images: data.images || [],
          listing_type: data.listingType || 'product',
          service_category: data.serviceCategory || null,
        })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      return { success: true };
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: async (vars?: any) => { await mutateAsync(vars).catch(() => {}); },
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useUpdateListing(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useDeleteListing(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { listingId } = vars;
      
      const { error: deleteError } = await supabase
        .from('listings')
        .delete()
        .eq('id', listingId);

      if (deleteError) throw deleteError;

      return { success: true };
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: async (vars?: any) => { await mutateAsync(vars).catch(() => {}); },
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useCreatePrinter(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = vars;
      console.log('Creating printer with data:', data);

      const { error: insertError } = await supabase
        .from('printers')
        .insert({
          user_id: data.userId,
          equipment_category: data.equipmentCategory,
          tool_or_service_type: data.toolOrServiceType,
          name: data.name,
          brand: data.brand,
          model: data.model,
          technology: data.technology,
          materials: data.materials,
          build_volume: data.buildVolume,
          price_per_hour: data.pricePerHour,
          price_per_gram: data.pricePerGram,
          description: data.description,
        });

      if (insertError) {
        console.error('Printer insert error:', insertError);
        throw insertError;
      }

      console.log('Printer created successfully');
      return { success: true };
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: async (vars?: any) => { await mutateAsync(vars).catch(() => {}); },
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useDeletePrinter(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { printerId } = vars;
      console.log('Deleting printer:', printerId);

      const { error: deleteError } = await supabase
        .from('printers')
        .delete()
        .eq('id', printerId);

      if (deleteError) {
        console.error('Printer delete error:', deleteError);
        throw deleteError;
      }

      console.log('Printer deleted successfully');
      return { success: true };
    } catch (e) {
      const err = e as Error;
      console.error('Printer deletion failed:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useUpdateUser(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { userId, data } = vars;
      console.log('Updating profile for userId:', userId);
      console.log('Update data:', data);

      const { error: updateError } = await supabase
        .from('users')
        .update({
          display_name: data.displayName,
          bio: data.bio,
          location: data.location,
          avatar_url: data.avatarUrl,
          shop_name: data.shopName,
          shop_mode: data.shopMode,
          shop_announcement: data.shopAnnouncement,
          brand_story: data.brandStory,
          website_url: data.websiteUrl,
          instagram_handle: data.instagramHandle,
          support_email: data.supportEmail,
          seller_tags: data.sellerTags,
          banner_url: data.bannerUrl,
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }
      return { success: true };
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: async (vars?: any) => { await mutateAsync(vars).catch(() => {}); },
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useListUsers() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // First, let's see what's in the sellers table
        const { data: allSellers, error: allSellersError } = await supabase
          .from('sellers')
          .select('*');
        
        console.log('All sellers in DB:', allSellers);
        console.log('All sellers error:', allSellersError);

        // Fetch sellers who have completed store setup (fallback to all if none completed)
        const { data: sellersData, error: sellersError } = await supabase
          .from('sellers')
          .select('*')
          .eq('store_setup_complete', true)
          .order('created_at', { ascending: false });

        if (sellersError) throw sellersError;

        let sellers = sellersData || [];
        
        // If no completed sellers, use all sellers
        if (sellers.length === 0 && allSellers && allSellers.length > 0) {
          console.log('No completed sellers found, using all sellers');
          sellers = allSellers;
        }

        console.log('Using sellers:', sellers);
        console.log('Sellers count:', sellers.length);
        
        // If we have sellers, fetch their profiles separately
        const userIds = sellers.map(s => s.user_id).filter(Boolean);
        let profilesMap = new Map();
        
        if (userIds.length > 0) {
          const profilesResult = await supabase
            .from('profiles')
            .select('*')
            .in('id', userIds);
          
          if (!profilesResult.error && profilesResult.data) {
            profilesResult.data.forEach((profile: any) => {
              profilesMap.set(profile.id, profile);
            });
          }
        }

        // Transform sellers to user format for the landing page
        const transformedUsers = sellers.map((seller: any) => {
          const profile = profilesMap.get(seller.user_id) || {};
          return {
            id: seller.id,
            user_id: seller.user_id,
            name: profile.display_name || seller.store_name || 'Unnamed Shop',
            displayName: profile.display_name || seller.store_name || 'Unnamed Shop',
            role: profile.role || 'seller',
            rating: '4.8', // Default rating
            orders: '156', // Default orders
            avatar: profile.avatar_url || '',
            avatar_url: profile.avatar_url || '',
            shop_name: seller.store_name || profile.display_name || 'Unnamed Shop',
          };
        });

        console.log('Transformed users:', transformedUsers);
        setData({ users: transformedUsers });
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err as Error);
        setData({ users: [] });
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  return { data, isLoading, error };
}

export function useListListings(options?: { limit?: number; offset?: number; sellerId?: string; userId?: string }) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchListings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let query = supabase
          .from('listings')
          .select(`
            *,
            sellers (
              id,
              store_name,
              user_id
            )
          `)
          .not('title', 'in', '(".kbj")')
          .not('description', 'in', '(";oiuhu")')
          .order('created_at', { ascending: false });

        if (options?.userId) {
          // Filter by seller's user_id directly
          query = query.eq('seller_id', options.userId);
        } else if (options?.sellerId) {
          query = query.eq('seller_id', options.sellerId);
        }

        if (options?.limit) {
          query = query.limit(options.limit);
        }

        const result = await query;
        console.log('Listings query result:', result);
        if (result.error) throw result.error;

        console.log('Raw listings data:', result.data);

        // Map database columns to component expectations
        const mappedListings = (result.data || []).map((listing: any) => ({
          id: listing.id,
          title: listing.title,
          description: listing.description,
          basePrice: listing.price,
          imageUrl: listing.images && listing.images.length > 0 ? listing.images[0] : null,
          images: listing.images || [],
          category: listing.category,
          stockQuantity: listing.stock,
          trackStock: listing.track_stock || false,
          estimatedDaysMin: listing.estimated_days_min || 3,
          estimatedDaysMax: listing.estimated_days_max || 7,
          shippingCost: listing.shipping_cost || 0,
          tags: listing.tags || [],
          listingType: listing.listing_type || 'product',
          serviceCategory: listing.service_category,
          serviceType: listing.service_type,
          sellerId: listing.seller_id,
          sellerName: listing.sellers?.store_name || 'Unknown Seller',
          isActive: listing.is_active !== false,
          views: listing.views || 0,
          createdAt: listing.created_at,
          updatedAt: listing.updated_at,
        }));

        setData({ listings: mappedListings });
      } catch (err) {
        setError(err as Error);
        setData({ listings: [] });
      } finally {
        setIsLoading(false);
      }
    };
    fetchListings();
  }, [options?.limit, options?.offset, options?.sellerId, options?.userId]);

  return { data, isLoading, error };
}

export function useGetListing(listingId?: string) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchListing = async () => {
      if (!listingId) return;
      
      setIsLoading(true);
      setError(null);
      try {
        const result = await supabase
          .from('listings')
          .select(`
            *,
            sellers (
              id,
              store_name,
              user_id
            )
          `)
          .eq('id', listingId)
          .single();

        if (result.error) throw result.error;

        // Map database columns to component expectations
        const mappedListing = {
          id: result.data.id,
          title: result.data.title,
          description: result.data.description,
          basePrice: result.data.price,
          imageUrl: result.data.images && result.data.images.length > 0 ? result.data.images[0] : null,
          images: result.data.images || [],
          category: result.data.category,
          stockQuantity: result.data.stock,
          trackStock: result.data.track_stock || false,
          estimatedDaysMin: result.data.estimated_days_min || 3,
          estimatedDaysMax: result.data.estimated_days_max || 7,
          shippingCost: result.data.shipping_cost || 0,
          tags: result.data.tags || [],
          listingType: result.data.listing_type || 'product',
          serviceCategory: result.data.service_category,
          serviceType: result.data.service_type,
          sellerId: result.data.seller_id,
          sellerName: result.data.sellers?.store_name || 'Unknown Seller',
          isActive: result.data.is_active,
          views: result.data.views,
          createdAt: result.data.created_at,
          updatedAt: result.data.updated_at,
        };

        setData(mappedListing);
      } catch (err) {
        setError(err as Error);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchListing();
  }, [listingId]);

  return { data, isLoading, error };
}

export function useListSellers(options?: { limit?: number; offset?: number }) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSellers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch sellers only (no joins to avoid 400 error)
        const sellersResult = await supabase
          .from('sellers')
          .select('*')
          .eq('store_setup_complete', true)
          .order('created_at', { ascending: false });

        if (sellersResult.error) throw sellersResult.error;

        const sellers = sellersResult.data || [];
        
        // If we have sellers, fetch their profiles separately
        const userIds = sellers.map(s => s.user_id).filter(Boolean);
        let profilesMap = new Map();
        
        if (userIds.length > 0) {
          const profilesResult = await supabase
            .from('profiles')
            .select('*')
            .in('id', userIds);
          
          if (!profilesResult.error && profilesResult.data) {
            profilesResult.data.forEach((profile: any) => {
              profilesMap.set(profile.id, profile);
            });
          }
        }

        // Merge sellers with profiles
        const transformedSellers = sellers.map((seller: any) => {
          const profile = profilesMap.get(seller.user_id) || {};
          return {
            id: seller.id,
            user_id: seller.user_id,
            displayName: profile.display_name || seller.store_name || 'Unnamed Shop',
            shopName: seller.store_name || profile.display_name || 'Unnamed Shop',
            avatarUrl: profile.avatar_url || '',
            avatar_url: profile.avatar_url || '',
            bannerUrl: profile.banner_url || '',
            banner_url: profile.banner_url || '',
            location: profile.location || '',
            sellerTags: profile.seller_tags || [],
            seller_tags: profile.seller_tags || [],
            shopMode: profile.shop_mode || 'both',
            shop_mode: profile.shop_mode || 'both',
            role: profile.role || 'seller',
            accepting_orders: seller.accepting_orders !== false,
            store_setup_complete: seller.store_setup_complete === true,
          };
        });

        setData({ sellers: transformedSellers });
      } catch (err) {
        console.error('Error fetching sellers:', err);
        setError(err as Error);
        setData({ sellers: [] });
      } finally {
        setIsLoading(false);
      }
    };
    fetchSellers();
  }, [options?.limit, options?.offset]);

  return { data, isLoading, error };
}

export function useGetUser(userId?: string | number) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) return;
    const fetchUser = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();
        if (result.error) throw result.error;
        setData(result.data);
      } catch (err) {
        setError(err as Error);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  return { data, isLoading, error };
}


export function useGetCart() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCart = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setData(null);
          setIsLoading(false);
          return;
        }
        
        const { data: cartData, error: cartError } = await supabase
          .from('carts')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (cartError && !cartError.message?.includes('not found')) throw cartError;
        setData(cartData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCart();
  }, []);

  return { data, isLoading, error };
}

export function useGetOrders() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setData(null);
          setIsLoading(false);
          return;
        }
        
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
        
        if (ordersError) throw ordersError;
        setData(ordersData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrders();
  }, []);

  return { data, isLoading, error };
}

function uniqueTruthy(values: Array<string | number | null | undefined>) {
  return Array.from(new Set(values.filter(Boolean).map(String)));
}

export function useListOrders(options?: { userId?: string | number }) {
  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        setData([]);
        return;
      }

      const activeUserId = String(options?.userId ?? user.id);
      const { data: ordersData, error: fetchError } = await supabase
        .from('orders')
        .select('*')
        .or(`buyer_id.eq.${activeUserId},seller_id.eq.${activeUserId}`)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      const loadedOrders = ordersData || [];
      const listingIds = uniqueTruthy(loadedOrders.map((order: any) => order.listing_id));
      const userIds = uniqueTruthy(loadedOrders.flatMap((order: any) => [order.buyer_id, order.seller_id]));

      const [{ data: listingsData }, { data: usersData }] = await Promise.all([
        listingIds.length
          ? supabase.from('listings').select('id,title,price,images').in('id', listingIds)
          : Promise.resolve({ data: [] as any[] }),
        userIds.length
          ? supabase.from('users').select('id,display_name,username,email').in('id', userIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const listingsById = new Map((listingsData || []).map((listing: any) => [String(listing.id), listing]));
      const usersById = new Map((usersData || []).map((profile: any) => [String(profile.id), profile]));

      const transformedOrders = loadedOrders.map((order: any) => {
        const listing = order.listing_id ? listingsById.get(String(order.listing_id)) : null;
        const buyer = order.buyer_id ? usersById.get(String(order.buyer_id)) : null;

        return {
          id: order.id,
          buyer_id: order.buyer_id,
          seller_id: order.seller_id,
          listing_id: order.listing_id,
          quantity: order.quantity || 1,
          total_amount: order.total_amount || order.price,
          status: order.status || 'pending',
          tracking_number: order.tracking_number,
          notes: order.notes,
          created_at: order.created_at,
          updated_at: order.updated_at,
          listings: listing,
          buyer,
          users: buyer,
          price: order.total_amount || order.price,
          amount: order.total_amount || order.price,
        };
      });

      setData(transformedOrders);
    } catch (e) {
      const err = e as Error;
      setError(err);
      console.error('Error fetching orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [options?.userId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { data, isLoading, error, refetch: fetchOrders };
}

export function useGetMessages() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setData(null);
          setIsLoading(false);
          return;
        }
        
        const { data: messagesData, error: messagesError } = await supabase
          .from('message_threads')
          .select('*')
          .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`)
          .order('updated_at', { ascending: false });
        
        if (messagesError) throw messagesError;
        setData(messagesData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMessages();
  }, []);

  return { data, isLoading, error };
}

export function useGetNotifications() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setData(null);
          setIsLoading(false);
          return;
        }
        
        const { data: notifData, error: notifError } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (notifError) throw notifError;
        setData(notifData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNotifications();
  }, []);

  return { data, isLoading, error };
}

export function useGetPrinters(userId?: string | number) {
  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPrinters = async () => {
      setIsLoading(true);
      try {
        let query = supabase.from('printers').select(`
        id,
        user_id,
        name,
        brand,
        model,
        equipment_category,
        technology,
        materials,
        build_volume,
        price_per_hour,
        price_per_gram,
        tool_or_service_type,
        image_url,
        is_active,
        equipment_status,
        equipment_group_id,
        created_at,
        updated_at
      `);
        if (userId) {
          query = query.eq('user_id', userId);
        }
        const { data: printersData, error: printersError } = await query.order('created_at', { ascending: false });
        
        if (printersError) throw printersError;
        setData(printersData || []);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPrinters();
  }, [userId]);

  return { data, isLoading, error };
}

export function useListReviews(options?: { userId?: string | number }) {
  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        setData([]);
        return;
      }

      let query = supabase
        .from('reviews')
        .select(`
          *,
          profiles!reviewer_id (
            id,
            display_name,
            username
          ),
          listings!listing_id (
            id,
            title
          )
        `)
        .order('created_at', { ascending: false });

      // Filter by user ID (as reviewee - seller receiving reviews)
      if (options?.userId) {
        query = query.eq('reviewee_id', options.userId);
      } else {
        query = query.eq('reviewee_id', user.id);
      }

      const result = await query;
      if (result.error) throw result.error;
      
      // Transform the data to match the expected format
      const transformedReviews = (result.data || []).map((review: any) => ({
        id: review.id,
        reviewee_id: review.reviewee_id,
        reviewer_id: review.reviewer_id,
        listing_id: review.listing_id,
        rating: review.rating,
        comment: review.comment,
        created_at: review.created_at,
        updated_at: review.updated_at,
        reviewer_name: review.users?.display_name || review.users?.username || 'Anonymous',
        reviewer: review.users,
        listing_title: review.listings?.title,
        listings: review.listings,
      }));

      setData(transformedReviews);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [options?.userId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  return { data, isLoading, error, refetch: fetchReviews };
}

export function useListPrinters(options?: { userId?: string | number }) {
  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPrinters = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        setData([]);
        return;
      }

      const { data: printers, error: fetchError } = await supabase
        .from('printers')
        .select(`
          id,
          user_id,
          name,
          brand,
          model,
          equipment_category,
          technology,
          materials,
          build_volume,
          price_per_hour,
          price_per_gram,
          tool_or_service_type,
          image_url,
          is_active,
          equipment_status,
          equipment_group_id,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setData(printers || []);
    } catch (e) {
      const err = e as Error;
      setError(err);
      console.error('Error fetching printers:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  return { data, isLoading, error, refetch: fetchPrinters };
}

export function useGetPortfolio(userId?: string | number) {
  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      setIsLoading(true);
      try {
        let query = supabase.from('portfolio').select('*');
        if (userId) {
          query = query.eq('user_id', userId);
        }
        const { data: portfolioData, error: portfolioError } = await query.order('created_at', { ascending: false });
        
        if (portfolioError) throw portfolioError;
        setData(portfolioData || []);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPortfolio();
  }, [userId]);

  return { data, isLoading, error };
}

export function useGetReviews() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchReviews = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setData(null);
          setIsLoading(false);
          return;
        }
        
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('*')
          .or(`reviewer_id.eq.${user.id},reviewee_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
        
        if (reviewsError) throw reviewsError;
        setData(reviewsData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReviews();
  }, []);

  return { data, isLoading, error };
}

export function useGetFavorites() {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchFavorites = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) {
          setData(null);
          setIsLoading(false);
          return;
        }
        
        const { data: favData, error: favError } = await supabase
          .from('favorites')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (favError) throw favError;
        setData(favData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFavorites();
  }, []);

  return { data, isLoading, error };
}

export async function toggleFavorite(userId: string, itemId: string, favoriteType: 'item' | 'shop') {
  try {
    // First check if it's already favorited
    const { data: existing } = await supabase
      .from('favorites')
      .select('*')
      .eq('user_id', userId)
      .eq(favoriteType === 'item' ? 'item_id' : 'shop_id', itemId)
      .eq('favorite_type', favoriteType)
      .single();

    if (existing) {
      // Remove favorite
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('id', existing.id);
      
      if (error) throw error;
      return { action: 'removed', success: true };
    } else {
      // Add favorite
      const favoriteData = {
        user_id: userId,
        favorite_type: favoriteType,
        ...(favoriteType === 'item' ? { item_id: itemId } : { shop_id: itemId })
      };

      const { error } = await supabase
        .from('favorites')
        .insert(favoriteData);
      
      if (error) throw error;
      return { action: 'added', success: true };
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
    return { action: 'error', success: false, error };
  }
}

export async function removeFavorite(favoriteId: string) {
  try {
    const { error } = await supabase
      .from('favorites')
      .delete()
      .eq('id', favoriteId);
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error removing favorite:', error);
    return { success: false, error };
  }
}

export async function createOrder(orderData: {
  buyer_id: string;
  seller_id: string;
  listing_id?: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  shipping_address?: any;
  notes?: string;
}) {
  try {
    const { data, error } = await supabase
      .from('orders')
      .insert({
        ...orderData,
        status: 'pending'
      })
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error creating order:', error);
    return { success: false, error };
  }
}

export async function updateOrderStatus(orderId: string, status: string, trackingNumber?: string) {
  try {
    const updateData: any = { status };
    if (trackingNumber) {
      updateData.tracking_number = trackingNumber;
    }
    
    const { data, error } = await supabase
      .from('orders')
      .update(updateData)
      .eq('id', orderId)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating order status:', error);
    return { success: false, error };
  }
}

export async function cancelOrder(orderId: string, reason?: string) {
  try {
    const updateData: any = { status: 'cancelled' };
    if (reason) {
      updateData.notes = reason;
    }
    
    const { data, error } = await supabase
      .from('orders')
      .update(updateData)
      .eq('id', orderId)
      .select()
      .single();
    
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error cancelling order:', error);
    return { success: false, error };
  }
}

export function useGetShop(userId?: string | number) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) return;
    
    const fetchShop = async () => {
      setIsLoading(true);
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (userError) throw userError;
        setData(userData);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchShop();
  }, [userId]);

  return { data, isLoading, error };
}

// Mutations
export function useAddToCart(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useRemoveFromCart(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useCreateOrder(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useSendMessage(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useCreateReview(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useCreateEquipmentGroup(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = vars;
      console.log('Creating equipment group with data:', data);

      const { data: { user } } = await supabase.auth.getUser();
      console.log('User ID:', user?.id);

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error: insertError } = await supabase
        .from('equipment_groups')
        .insert({
          user_id: user.id,
          name: data.name,
          description: data.description,
          category: data.category,
        });

      if (insertError) {
        console.error('Equipment group insert error:', insertError);
        throw insertError;
      }

      console.log('Equipment group created successfully');
      return { success: true };
    } catch (e) {
      const err = e as Error;
      console.error('Equipment group creation failed:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useDeleteEquipmentGroup(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { groupId } = vars;
      console.log('Deleting equipment group:', groupId);

      const { error: deleteError } = await supabase
        .from('equipment_groups')
        .delete()
        .eq('id', groupId);

      if (deleteError) {
        console.error('Equipment group delete error:', deleteError);
        throw deleteError;
      }

      console.log('Equipment group deleted successfully');
      return { success: true };
    } catch (e) {
      const err = e as Error;
      console.error('Equipment group deletion failed:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useListEquipmentGroups() {
  const [error, setError] = useState<Error | null>(null);

  const fetchGroups = async () => {
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: groups, error: fetchError } = await supabase
        .from('equipment_groups')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      return groups || [];
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching equipment groups:', err);
      return [];
    }
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['equipment-groups'],
    queryFn: fetchGroups,
  });

  return { data, isLoading, error, refetch };
}

export function useListShippingProfiles() {
  const [error, setError] = useState<Error | null>(null);

  const fetchProfiles = async () => {
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profiles, error: fetchError } = await supabase
        .from('profiles')
        .select('id, domestic_shipping_cost, europe_shipping_cost, north_america_shipping_cost, international_shipping_cost, free_shipping_threshold, selling_regions')
        .eq('id', user.id)
        .single();

      if (fetchError) throw fetchError;

      if (!profiles) return [];

      // Convert user profile data to shipping profile format
      return [{
        id: profiles.id,
        name: 'Default Shipping Profile',
        domesticCost: profiles.domestic_shipping_cost || 5.99,
        europeCost: profiles.europe_shipping_cost || 12.99,
        northAmericaCost: profiles.north_america_shipping_cost || 8.99,
        internationalCost: profiles.international_shipping_cost || 19.99,
        freeShippingThreshold: profiles.free_shipping_threshold || 50,
        shippingRegions: profiles.selling_regions || ['US'],
      }];
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching shipping profiles:', err);
      return [];
    }
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['shipping-profiles'],
    queryFn: fetchProfiles,
  });

  return { data, isLoading, error, refetch };
}

export function useUpdateEquipmentGroup(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { groupId, data } = vars;
      console.log('Updating equipment group:', groupId, data);

      const { error: updateError } = await supabase
        .from('equipment_groups')
        .update({
          name: data.name,
          description: data.description,
          category: data.category,
        })
        .eq('id', groupId);

      if (updateError) {
        console.error('Equipment group update error:', updateError);
        throw updateError;
      }

      console.log('Equipment group updated successfully');
      return { success: true };
    } catch (e) {
      const err = e as Error;
      console.error('Equipment group update failed:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useUpdatePrinter(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const { printerId, data } = vars;
      console.log('Updating printer:', printerId, data);

      const { error: updateError } = await supabase
        .from('printers')
        .update({
          name: data.name,
          brand: data.brand,
          model: data.model,
          technology: data.technology,
          materials: data.materials,
          build_volume: data.buildVolume,
          price_per_hour: data.pricePerHour,
          price_per_gram: data.pricePerGram,
          description: data.description,
          is_active: data.isActive,
          equipment_group_id: data.equipmentGroupId,
        })
        .eq('id', printerId);

      if (updateError) {
        console.error('Printer update error:', updateError);
        throw updateError;
      }

      console.log('Printer updated successfully');
      return { success: true };
    } catch (e) {
      const err = e as Error;
      console.error('Printer update failed:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useGetPrinter() {
  return {
    data: null,
    isLoading: false,
    error: null,
  };
}

export function useListSponsoredShops() {
  const [data, setData] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSponsored = async () => {
      setIsLoading(true);
      try {
        const { data: sponsoredData, error: sponsoredError } = await supabase
          .from('users')
          .select('*')
          .eq('sponsorship_active', true)
          .limit(10);
        
        if (sponsoredError) throw sponsoredError;
        setData(sponsoredData || []);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSponsored();
  }, []);

  return { data, isLoading, error };
}

export function useCreateSponsoredShop(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useUpdateListingStatus(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useUpdatePrinterStatus(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useUpdateOrderStatus(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useCancelOrder(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useMarkOrderShipped(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useMarkOrderDelivered(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useListEquipment() {
  const [error, setError] = useState<Error | null>(null);

  const fetchEquipment = async () => {
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: equipment, error: fetchError } = await supabase
        .from('printers')
        .select(`
          id,
          user_id,
          name,
          brand,
          model,
          equipment_category,
          technology,
          materials,
          build_volume,
          price_per_hour,
          price_per_gram,
          tool_or_service_type,
          image_url,
          is_active,
          equipment_status,
          equipment_group_id,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;
      return equipment || [];
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching equipment:', err);
      return [];
    }
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['equipment'],
    queryFn: fetchEquipment,
  });

  return { data, isLoading, error, refetch };
}

export function useGetEquipment() {
  return {
    data: null,
    isLoading: false,
    error: null,
  };
}

export function useCreateEquipment(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useUpdateEquipment(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export function useDeleteEquipment(): MutationReturn {
  return {
    mutate: async () => {},
    mutateAsync: async () => ({}),
    isLoading: false,
    isPending: false,
    error: null,
  };
}

export type CreateUserRequestRole = 'buyer' | 'seller' | 'both';

// Query key helpers for React Query
export function getGetListingQueryKey(listingId: string | number) {
  return ['listings', listingId];
}

export function getGetUserQueryKey(userId: string | number) {
  return ['users', userId];
}

export function getListListingsQueryKey() {
  return ['listings'];
}

export function getListUsersQueryKey() {
  return ['users'];
}

export function getListOrdersQueryKey() {
  return ['orders'];
}

export function getListPrintersQueryKey() {
  return ['printers'];
}

export function getGetPrinterQueryKey(printerId: string | number) {
  return ['printers', printerId];
}

export function getGetOrdersQueryKey() {
  return ['orders'];
}

export function getListPortfolioQueryKey(userId: string | number) {
  return ['portfolio', userId];
}

export function getListReviewsQueryKey(userId: string | number) {
  return ['reviews', userId];
}

export function getGetCartQueryKey() {
  return ['cart'];
}

export function getGetMessagesQueryKey() {
  return ['messages'];
}

export function getGetNotificationsQueryKey() {
  return ['notifications'];
}

export function getListContestsQueryKey() {
  return ['contests'];
}

export function getGetContestQueryKey(contestId: string) {
  return ['contests', contestId];
}

export function getListContestEntriesQueryKey(contestId: string) {
  return ['contests', contestId, 'entries'];
}

// Contest hooks
export function useListContests() {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchContests() {
      try {
        setIsLoading(true);
        const { contests } = await import('./contest-api').then(m => m.listContests());
        setData(contests);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContests();
  }, []);

  return { data, isLoading, error };
}

export function useGetContest(contestId: string) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchContest() {
      try {
        setIsLoading(true);
        const { contest } = await import('./contest-api').then(m => m.getContest(contestId));
        setData(contest);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    }
    if (contestId) fetchContest();
  }, [contestId]);

  return { data, isLoading, error };
}

export function useListContestEntries(contestId: string) {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchEntries() {
      try {
        setIsLoading(true);
        const { entries } = await import('./contest-api').then(m => m.listContestEntries(contestId));
        setData(entries);
      } catch (e) {
        setError(e as Error);
      } finally {
        setIsLoading(false);
      }
    }
    if (contestId) fetchEntries();
  }, [contestId]);

  return { data, isLoading, error };
}

export function useCreateContestEntry(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      await import('./contest-api').then(m => m.createContestEntry(vars));
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useVoteForEntry(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      await import('./contest-api').then(m => m.voteForEntry(vars.entryId, vars.userId));
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

export function useCreateContest(): MutationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (vars: any) => {
    setIsLoading(true);
    setError(null);
    try {
      await import('./contest-api').then(m => m.createContest(vars));
    } catch (e) {
      const err = e as Error;
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate: (vars: any) => mutateAsync(vars),
    mutateAsync,
    isLoading,
    isPending: isLoading,
    error,
  };
}

